import PropTypes from 'prop-types';
import { memo } from 'react';
import { ProgressBar } from './ProgressBar.js';
import { ProjectCost } from './ProjectCost.js';
import { AddToProjectFavoritesWidget, FAVORITES_STYLE } from './addToProjectFavoritesWidget.js';
import { GiveWidget } from './GiveWidget.js';
import { hasMatch, makeTeacherAndSchoolInfo } from './projectUtils.js';
import { convertSmallPhotoToMediumSmall } from '../utils/userUtils.js';
import { projectFormatter } from './projectFormatter.js';
import Html5Entities from '../../external/npm/html5-entities.js';

/* global userId */

export let SmallProjectCard = function ({ project, showGiveWidget, prepopulatedDonationAmountForGiveWidget, giveWidgetSubmitTakesUserDirectlyToCart, className, amountGiven, onProjectCardClick, altViewForFullyFundedProjects }) {
    const defaultImage = baseCacheURL + 'images/classroom/fallback-photo1@2x.png';
    const proposalURL = projectFormatter.removeUTMParams(project.proposalURL, project.teacherId);
    const imageURL = project.imageURL || defaultImage;
    const retinaImageURL = convertSmallPhotoToMediumSmall(project.imageURL) || defaultImage;
    const incomplete = parseFloat(project.costToComplete) > 0;

    const makeMatchExplanation = function () {
        if (hasMatch(project)) {
            const multiplier = project.matchingFund.multipleForDisplay;
            const badgeClass = project.matchingFund.multipleForDisplay === '1.5X'
                ? 'onepointfive'
                : 'DYI';
            return (
                <span className="match-offer-explanation">
                    <span className={'match-offer-badge ' + badgeClass}>
                        <span className="match-offer-badge-content">{multiplier}</span>
                    </span>
                    <strong>Donations matched!</strong>
                </span>
            );
        }
    };

    const onClick = function (event) {
        if (onProjectCardClick) {
            onProjectCardClick(parseInt(event.currentTarget.getAttribute('data-project-id')));
        }
    };

    const reformatMyStudentsNeed = function (blurb, teacherName) {
        const decodedName = Html5Entities.decode(teacherName);
        const initialTrimmedBlurb = blurb ? blurb.trim() : '';

        // --- First Replacement: Initial funding phrase ---
        const fundingPattern = /^(I need|Help me give my students|My students need)/i;
        const fundingReplacementText = `${decodedName} and their classroom received`;

        let processedBlurb = fundingPattern.test(initialTrimmedBlurb)
            ? initialTrimmedBlurb.replace(fundingPattern, fundingReplacementText)
            : initialTrimmedBlurb;

        // --- Second Replacement: Possessive adjectives ---
        const possessivePattern = /\b(my|his|her|our)\b/gi;
        processedBlurb = processedBlurb.replace(possessivePattern, (matchedAdjective) => {
            if (matchedAdjective.length > 0 && matchedAdjective[0] === matchedAdjective[0].toUpperCase()) {
                return 'Their';
            } else {
                return 'their';
            }
        });

        // --- Third Replacement: Personal pronouns ---
        const personalPronounMap = {
            // Subject forms -> "they"/"They"
            i: ['they', 'They'],
            you: ['they', 'They'],
            he: ['they', 'They'],
            she: ['they', 'They'],
            we: ['they', 'They'],
            // Object forms -> "them"/"Them"
            me: ['them', 'Them'],
            him: ['them', 'Them'],
            her: ['them', 'Them'],
            us: ['them', 'Them'],
        };

        // Regex to find all personal pronouns
        const personalPronounPattern = /\b(i|he|she|we|me|him|her|us)\b/gi;

        processedBlurb = processedBlurb.replace(personalPronounPattern, (matchedPronoun) => {
            const lowerMatchedPronoun = matchedPronoun.toLowerCase();
            if (Object.prototype.hasOwnProperty.call(personalPronounMap, lowerMatchedPronoun)) {
                const [replacementLower, replacementUpper] = personalPronounMap[lowerMatchedPronoun];
                if (matchedPronoun.length > 0 && matchedPronoun[0] === matchedPronoun[0].toUpperCase()) {
                    return replacementUpper;
                } else {
                    return replacementLower;
                }
            }
            return matchedPronoun; // Should not be reached if regex matches keys in map
        });

        return <span>{processedBlurb}</span>;
    };

    const renderShowGiveWidget = function () {
        if (showGiveWidget) {
            return (
                <GiveWidget
                    showGiveWidget={showGiveWidget}
                    project={project}
                    projectId={project.id}
                    prepopulatedDonationAmount={prepopulatedDonationAmountForGiveWidget}
                    submitTakesUserDirectlyToCart={giveWidgetSubmitTakesUserDirectlyToCart}
                    hideCompleteProjectCallout={true}
                />
            );
        }
    };

    const formatShortenedDate = function (date) {
        if (date) {
            return new Date(date).toLocaleDateString('en-us');
        }
        return '';
    };

    return (
        <div className={'col col4' + (className ? ' ' + className : '')}>
            {!altViewForFullyFundedProjects
                ? <div className={'project-card-small ' + (hasMatch(project) ? 'matched' : '')}>
                    <a className='project-page-link' href={proposalURL} data-project-id={project.id} onClick={onClick}/>
                    <div
                        className="classroom-photo"
                        style={{
                            backgroundImage: `image-set(url(${imageURL}) 1x, url(${retinaImageURL}) 2x)`,
                        }}
                    >
                        <AddToProjectFavoritesWidget
                            projectId={parseInt(project.id)}
                            isEligibleForFunding={incomplete}
                            initFavStatus={false}
                            userId={parseInt(userId)}
                            favoritesStyle={FAVORITES_STYLE.heart}
                        />
                        <div className="project-title"
                            dangerouslySetInnerHTML={{ __html: project.title }}>
                        </div>
                    </div>
                    {!(project.fundingStatus === 'funded')
                        ? <div className="project-description">
                            {projectFormatter.formatMyStudentsNeed(project)}
                        </div>
                        : ''}
                    {project.fundingStatus === 'funded'
                        ? <div className="project-fully-funded">
                            {'Fully Funded ' + formatShortenedDate(project.fullyFundedDate)}
                        </div>
                        : ''}
                    <div className="teacher-info">
                        {makeTeacherAndSchoolInfo(project)}
                    </div>
                    <div className="project-data">
                        <ProjectCost project={project} amountGiven={amountGiven}/>
                        {project.fundingStatus === 'funded'
                            ? <div className="student-impact">
                                <b>{project.numStudents}</b> students impacted
                            </div>
                            : '' }
                    </div>
                    {project.fundingStatus === 'funded' ? '' : <ProgressBar project={project} /> }
                    {makeMatchExplanation()}
                </div>
                : <a className="fully-funded" href={proposalURL} data-project-id={project.id} onClick={onClick}>
                    <div className="foreground">
                        <div className="fully-funded-text">Fully Funded!</div>
                        <div className="funding-date">{formatShortenedDate(project.fullyFundedDate)}</div>
                        <div className="description">{reformatMyStudentsNeed(projectFormatter.formatMyStudentsNeed(project), project.teacherName)}</div>
                        <div className="by-line"><ProjectCost project={project} amountGiven={amountGiven}/> raised by {makeTeacherAndSchoolInfo(project)}</div>
                        <div className="fully-funded-bar"></div>
                    </div>
                </a>
            }
            {renderShowGiveWidget()}
        </div>
    );
};

SmallProjectCard.propTypes = {
    project: PropTypes.object.isRequired,
    showGiveWidget: PropTypes.bool,
    prepopulatedDonationAmountForGiveWidget: PropTypes.number,
    giveWidgetSubmitTakesUserDirectlyToCart: PropTypes.bool,
    amountGiven: PropTypes.number,
    className: PropTypes.string,
    onProjectCardClick: PropTypes.func,
    altViewForFullyFundedProjects: PropTypes.bool,
};

SmallProjectCard = memo(SmallProjectCard);
